import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import api from "../api/API";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";

import "./PlanejamentoDiaModal.css";

const PlanejamentoDiaModal = ({
  show,
  onHide,
  dia,
  onSaved
}) => {

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [jaPlanejado, setJaPlanejado] = useState(false);
  const [filtro, setFiltro] = useState("");
  const inputRef = useRef(null);

  // PASSO 1
  const [reflexao, setReflexao] = useState("");

  // PASSO 2
  const [tarefasDisponiveis, setTarefasDisponiveis] = useState([]);
  const [prioridadesSelecionadas, setPrioridadesSelecionadas] = useState([]);

  // PASSO 3
  const [secundarias, setSecundarias] = useState([]);
  const [novaSecundaria, setNovaSecundaria] = useState("");

  useEffect(() => {
    if (show && dia?.id) {
      inicializarModal();
      inputRef.current?.focus();
    }
  }, [show]);

  async function inicializarModal() {
    resetState();

    try {

      console.log("Dia-ID:", dia.id);

      const [resDia, resTarefas, resTarefasDia] = await Promise.all([
        api.get(`/dias/by-id/${dia.id}`),
        api.get("/tasks"),
        api.get(`/dias/${dia.id}/tarefas`)
      ]);

      setTarefasDisponiveis(resTarefas.data);

      setReflexao(resDia.data.reflexao || "");

      const prioridades = resTarefasDia.data
        .filter(t => t.tipo !== 3)
        .map(t => t.id_tarefa);

      setPrioridadesSelecionadas(prioridades);

      const secundariasDia = resTarefasDia.data
        .filter(t => t.tipo === 3);

      setSecundarias(secundariasDia);

      // Definir step inteligente
      if (resDia.data.reflexao && prioridades.length > 0) {
        setStep(3);
        setJaPlanejado(true);
      } else if (resDia.data.reflexao) {
        setStep(2);
      } else {
        setStep(1);
      }

    } catch (error) {
      console.error("Erro ao carregar planejamento", error);
    }
  }

  function resetState() {
    setStep(1);
    setReflexao("");
    setPrioridadesSelecionadas([]);
    setSecundarias([]);
    setNovaSecundaria("");
    setJaPlanejado(false);
  }

  function togglePrioridade(id) {
    if (jaPlanejado) return;

    if (prioridadesSelecionadas.includes(id)) {
      setPrioridadesSelecionadas(
        prioridadesSelecionadas.filter(p => p !== id)
      );
    } else {
      if (prioridadesSelecionadas.length < 3) {
        setPrioridadesSelecionadas([...prioridadesSelecionadas, id]);
      }
    }
  }

  async function removerPrioridade(idTarefa) {
    try {
      await api.delete(`/dias/${dia.id}/tarefas/${idTarefa}`);
      setPrioridadesSelecionadas(
        prioridadesSelecionadas.filter(p => p !== idTarefa)
      );
    } catch (error) {
      console.error("Erro ao remover prioridade", error);
    }
  }

  function adicionarSecundaria() {
    if (!novaSecundaria.trim() || jaPlanejado) return;

    setSecundarias([
      ...secundarias,
      { descricao: novaSecundaria }
    ]);

    setNovaSecundaria("");

    inputRef.current?.focus();
  }

  async function removerSecundaria(idTarefa) {
    try {
      await api.delete(`/tasks/${idTarefa}`);
      setSecundarias(
        secundarias.filter(s => s.id !== idTarefa)
      );
    } catch (error) {
      console.error("Erro ao remover secundária", error);
    }
  }

  async function salvarPlanejamento() {
    if (!dia?.id) return;

    setLoading(true);

    try {

      // Reflexão
      await api.put(`/dias/${dia.id}/reflexao`, {
        reflexao
      });


      const resTarefasDia = await api.get(`/dias/${dia.id}/tarefas`);
      const tarefasAtuais = resTarefasDia.data
        .filter(t => t.tipo !== 3)
        .map(t => t.id_tarefa);

      const paraInserir = prioridadesSelecionadas.filter(
        id => !tarefasAtuais.includes(id)
      );

      const paraRemover = tarefasAtuais.filter(
        id => !prioridadesSelecionadas.includes(id)
      );



      // Prioridades
      for (const idTarefa of paraInserir) {
        await api.post(`/dias/${dia.id}/tarefas`, {
          id_tarefa: idTarefa
        });
      }

      for (const idTarefa of paraRemover) {
        await api.delete(`/dias/${dia.id}/tarefas/${idTarefa}`);
      }

      // Secundárias
      for (const s of secundarias) {
        if (!s.id) {
          await api.post(`/dias/${dia.id}/secundaria`, {
            descricao: s.descricao
          });
        }
      }

      onSaved();
      onHide();

    } catch (error) {
      console.error("Erro ao salvar planejamento", error);
    }

    setLoading(false);
  }


  function normalizar(texto) {
    return texto
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }


  const tarefasFiltradas = tarefasDisponiveis.filter(t => {
    
    if (!filtro.trim()) return true;

    const descricao = normalizar(t.descricao.toLowerCase());

    // separa por espaços
    const termos = normalizar(filtro)
      .toLowerCase()
      .split(" ")
      .filter(term => term.trim() !== "");

    // todos os termos precisam existir
    return termos.every(term => descricao.includes(term));
    
});

  return (
    <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="planejamento-modal-dialog">

      <Modal.Header closeButton>
        <Modal.Title>Planejamento do Dia</Modal.Title>
        <span className="ms-auto text-muted small">
          Passo {step}/3
        </span>
      </Modal.Header>

      <Modal.Body className="mt-2 planejamento-card foco">

        {jaPlanejado && (
          <Alert variant="success">
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            Você já definiu seu planejamento para hoje.
          </Alert>
        )}

        {/* PASSO 1 */}
        {step === 1 && (
          <>
            <h6 className="mb-3">
              O que precisa acontecer hoje para você sentir que avançou de verdade?
            </h6>

            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Descreva o progresso mais importante para hoje..."
              value={reflexao}
              disabled={jaPlanejado}
              onChange={(e) => setReflexao(e.target.value)}
            />
          </>
        )}

        {/* PASSO 2 */}
        {step === 2 && (
          <>
            <div className="d-flex justify-content-between mb-2">
              <h6>Prioridades do dia (máx 3)</h6>
              <small>{prioridadesSelecionadas.length}/3</small>
            </div>

            {/* CAMPO DE FILTRO */}
            <Form.Control
              type="text"
              placeholder="Filtrar tarefas..."
              className="mb-3"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              disabled={jaPlanejado}
            />

            <div style={{ maxHeight: 350, overflowY: "auto" }}>
              {tarefasFiltradas.map(t => {
                const selected = prioridadesSelecionadas.includes(t.id);

                return (
                  <div
                    key={t.id}
                    className={`prioridade-item ${selected ? "selected" : ""}`}
                    onClick={() => togglePrioridade(t.id)}
                    style={{
                      cursor: jaPlanejado ? "default" : "pointer",
                      padding: "10px",
                      borderRadius: "8px",
                      marginBottom: "6px",
                      transition: "0.2s"
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{t.descricao}</span>

                      {selected && !jaPlanejado && (
                        <Button
                          size="sm"
                          variant="link"
                          onClick={(e) => {
                            e.stopPropagation();
                            removerPrioridade(t.id);
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* PASSO 3 */}
        {step === 3 && (
          <>
            <h6 className="mb-3">Atividades secundárias</h6>

            {!jaPlanejado && (
              <div className="d-flex gap-2 mb-3">
                <Form.Control
                  ref={inputRef}
                  placeholder="Nome da atividade"
                  value={novaSecundaria}
                  onChange={(e) => setNovaSecundaria(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter"){
                      e.preventDefault();
                      adicionarSecundaria();
                    }
                  }}
                />
                <Button variant="success" 
                  onClick={adicionarSecundaria}
                  disabled={!novaSecundaria.trim()}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </Button>
              </div>
            )}

            {secundarias.map((s) => (
              <div
                key={s.id || s.descricao}
                className="d-flex justify-content-between align-items-center mb-2"
              >
                <span>• {s.descricao}</span>

                {!jaPlanejado && s.id && (
                  <Button
                    size="sm"
                    variant="link"
                    onClick={() => removerSecundaria(s.id)}
                  >
                    ❌
                  </Button>
                )}
              </div>
            ))}
          </>
        )}

      </Modal.Body>

      <Modal.Footer>

        {jaPlanejado && (
          <Button
            variant="outline-secondary"
            onClick={() => setJaPlanejado(false)}
          >
            Editar Planejamento
          </Button>
        )}

        {step > 1 && (
          <Button variant="light" onClick={() => setStep(step - 1)}>
            Voltar
          </Button>
        )}

        {step < 3 ? (
          <Button
            variant="success"
            onClick={() => setStep(step + 1)}
          >
            Continuar
          </Button>
        ) : (
          <Button
            variant="success"
            onClick={salvarPlanejamento}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Salvando...
              </>
            ) : (
              "Finalizar Planejamento"
            )}
          </Button>
        )}

      </Modal.Footer>

    </Modal>
  );
};

export default PlanejamentoDiaModal;
